import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaUser,
  FaLock,
  FaEnvelope,
  FaMusic,
  FaHeadphones,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import "./Auth.css";
import { signup } from "./helper/auth.helper";

import { useAuth } from "../../contexts/AuthContext";

const Signup = () => {
  useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Basic validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      const response = await signup({
        email: formData.email,
        password: formData.password,
        name: formData.name,
      });

      if (response.message === "User already exists" && response.data?.userId) {
        setError("User already exists. Either login or use a different email.");
        setIsLoading(false);
        return;
      }

      // If verification is pending, redirect to VerifyEmail page
      if (response.status === "PENDING_VERIFICATION") {
        navigate("/verify-email", {
          state: {
            email: formData.email,
            name: formData.name,
            password: formData.password,
          },
        });
      } else {
        setError("Unexpected response from server. Please try again.");
      }
    } catch (err) {
      console.error("Signup error:", err);
      // Show user-friendly error for Firebase auth errors
      if (err?.message && err.message.includes("auth/email-already-in-use")) {
        setError(
          "This email is already registered. Please log in or use another email."
        );
      } else if (err?.message && err.message.includes("auth/")) {
        setError("Signup failed. Please check your details and try again.");
      } else {
        setError(err.message || "Failed to create account. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      className="auth-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="auth-background">
        <div className="music-note note-1">
          <FaMusic />
        </div>
        <div className="music-note note-2">
          <FaHeadphones />
        </div>
        <div className="music-note note-3">
          <FaMusic />
        </div>
      </div>

      <motion.div
        className="auth-card"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <div className="auth-header">
          <motion.h2
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
            }}
          >
            Join the Rhythm
          </motion.h2>
          <p>Create an account to start your musical journey</p>
        </div>

        {error && (
          <motion.div
            className="auth-error"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error}
          </motion.div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <FaUser className="input-icon" />
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <FaEnvelope className="input-icon" />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <FaLock className="input-icon" />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <FaLock className="input-icon" />
            <input
              type="password"
              name="confirmPassword"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-options">
            {/* <label className="terms-checkbox">
              <input type="checkbox" required /> I agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#3498db", textDecoration: "none" }}
              >
                Terms & Conditions
              </a>
            </label> */}
          </div>

          <motion.button
            type="submit"
            className="auth-button"
            disabled={isLoading}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? "Creating Account..." : "Sign Up"}
          </motion.button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account? <Link to="/login">Sign In</Link>
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Signup;
